










































import { defineComponent, PropType } from '@vue/composition-api';
import query from '@/hsc-api/queries/AlertTicketsByHeatingSystemIdQuery.gql';
import Table from '@/features/ui/table/Table.global.vue';
import Select from '@/components/form/Select.global.vue';
import { APOLLO_CLIENT } from '@/features/core/container/model';
import { excludeHeatingSystemComponentNameList, getHeatingSystemComponentName } from '@/util/heating-systems';
import { ComparisonValue } from '@/types/hsc-types';
import Pagination from '@/features/ui/pagination/Pagination.global.vue';
import RelativeTime from '@/features/ui/datetime/RelativeTime.global.vue';
import {
  AlertTicketsByHeatingSystemIdQuery,
  AlertTicketsByHeatingSystemIdQuery_alertTicketsByHeatingSystemId,
  AlertTicketsByHeatingSystemIdQuery_alertTicketsByHeatingSystemId_alertMeasurementRules,
  AlertTicketsByHeatingSystemIdQueryVariables,
} from '@/hsc-api/queries/__generated__/AlertTicketsByHeatingSystemIdQuery';
import TableSkeleton from '@/features/ui/table/TableSkeleton.global.vue';
import { HeatingSystem } from './HeatingSystemsAdvancedView.vue';
import { HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items_heatingSystemMeasurementGroups_heatingSystemMeasurements } from '@/hsc-api/queries/__generated__/HeatingSystemCollectionWithPaginationQuery';

type AlertRow = {
  start: Date;
  end: Date | null;
  status: string;
  component: string;
  details: string;
};

type ComponentOption = { value: string; label: string };

export default defineComponent({
  name: 'AlertsTab',
  components: {
    Table,
    TableSkeleton,
    RelativeTime,
    Select,
    Pagination,
  },
  props: {
    heatingSystem: {
      type: Object as PropType<HeatingSystem>,
      required: true,
    },
  },
  data() {
    return {
      alerts: [] as AlertTicketsByHeatingSystemIdQuery_alertTicketsByHeatingSystemId[],
      selectedComponent: { value: 'Alle', label: 'Alle' } as ComponentOption,
      currentPage: 1 as number,
      pageSize: 15 as number,
      columns: [
        { name: 'start', label: 'Start' },
        { name: 'end', label: 'Ende' },
        { name: 'status', label: 'Status' },
        { name: 'component', label: 'Komponente' },
        { name: 'details', label: 'Details' },
      ],
    };
  },
  computed: {
    rows(this: {
      alerts: AlertTicketsByHeatingSystemIdQuery_alertTicketsByHeatingSystemId[];
      selectedComponent: { value: string; label: string };
      heatingSystem: HeatingSystem;
      getComponentTypeBySpotId: (spotId: string) => string;
      getDetailsForAlert: (alert: AlertTicketsByHeatingSystemIdQuery_alertTicketsByHeatingSystemId) => string;
    }): AlertRow[] {
      const alerts = this.alerts;
      return (
        alerts
          .filter((alert) => {
            // Component filter
            if (this.selectedComponent.value !== 'Alle') {
              const group = this.heatingSystem?.heatingSystemMeasurementGroups?.find((g) => g.spotId === alert.spotId);
              if (!group || group.systemComponentTypeName !== this.selectedComponent.value) return false;
            }
            return true;
          })
          .map((alert) => {
            return {
              start: alert.createdAt,
              end: null,
              status: alert.ticketStatus === 'OPENED' ? 'Aktiv' : 'Geschlossen',
              component: this.getComponentTypeBySpotId(alert.spotId || ''),
              details: this.getDetailsForAlert(alert),
            };
          }) || []
      );
    },
    componentOptions(): Array<{ value: string; label: string }> {
      if (!this.heatingSystem || !this.heatingSystem.heatingSystemMeasurementGroups) {
        return [{ value: 'Alle', label: 'Alle' }];
      }
      const types: string[] = this.heatingSystem.heatingSystemMeasurementGroups
        .map((group) => group.systemComponentTypeName)
        .filter(Boolean);

      // Exclude types in excludeHeatingSystemComponentNameList
      const uniqueTypes = Array.from(new Set(types)).filter((type) => {
        // Normalize to uppercase and compare
        return !excludeHeatingSystemComponentNameList.includes(
          type.toUpperCase() as typeof excludeHeatingSystemComponentNameList[number],
        );
      });

      const options = [
        { value: 'Alle', label: 'Alle' },
        ...uniqueTypes.map((type) => ({
          value: type,
          label: getHeatingSystemComponentName(type),
        })),
      ];
      return options;
    },
    paginatedRows(this: { currentPage: number; pageSize: number; rows: AlertRow[] }): AlertRow[] {
      const start: number = (this.currentPage - 1) * this.pageSize;
      return this.rows.slice(start, start + this.pageSize);
    },
    totalPages(this: { currentPage: number; pageSize: number; rows: AlertRow[] }): number {
      return Math.ceil(this.rows.length / this.pageSize) || 1;
    },
  },
  methods: {
    getMeasurementLabelById(measurementId: string): string {
      if (this.heatingSystem && Array.isArray(this.heatingSystem.heatingSystemMeasurementGroups)) {
        for (const group of this.heatingSystem.heatingSystemMeasurementGroups) {
          if (Array.isArray(group.heatingSystemMeasurements)) {
            const measurement = group.heatingSystemMeasurements.find(
              (
                measurementObj: HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items_heatingSystemMeasurementGroups_heatingSystemMeasurements,
              ) => measurementObj.id === measurementId,
            );
            if (measurement && measurement.metricLabel) {
              return measurement.metricLabel;
            }
          }
        }
      }
      return 'Unbekannt';
    },

    getDetailsForAlert(
      this: {
        formatRuleMessage: (toCompareValue: number, comparisonValue: ComparisonValue, metricLabel: string) => string;
        getMeasurementLabelById: (measurementId: string) => string;
      },
      alert: AlertTicketsByHeatingSystemIdQuery_alertTicketsByHeatingSystemId,
    ): string {
      let metricLabel = 'Unbekannt';
      let toCompareValue = 0;
      let comparisonValue = ComparisonValue.GREATER;

      const rule =
        alert.alertMeasurementRules as AlertTicketsByHeatingSystemIdQuery_alertTicketsByHeatingSystemId_alertMeasurementRules | null;
      if (rule && typeof rule.heatingSystemMeasurementId === 'string') {
        toCompareValue = rule.toCompareValue ?? 0;
        comparisonValue = rule.comparisonValue ?? ComparisonValue.GREATER;
        metricLabel = this.getMeasurementLabelById(rule.heatingSystemMeasurementId as string);
      } else if (alert.alertDefaultMeasurementRules) {
        const defaultRule = alert.alertDefaultMeasurementRules;
        toCompareValue = defaultRule.toCompareValue ?? 0;
        comparisonValue = defaultRule.comparisonValue ?? ComparisonValue.GREATER;
        metricLabel = defaultRule.metricLabel || 'Unbekannt';
      }

      return this.formatRuleMessage(toCompareValue, comparisonValue, metricLabel);
    },
    formatRuleMessage(toCompareValue: number, comparisonValue: ComparisonValue, metricLabel: string): string {
      if (toCompareValue != null && metricLabel && comparisonValue) {
        let comparisonDescription = '';
        switch (comparisonValue) {
          case ComparisonValue.EQUAL:
            comparisonDescription = 'erreicht';
            break;
          case ComparisonValue.GREATER:
            comparisonDescription = 'überschritten';
            break;
          case ComparisonValue.LOWER:
            comparisonDescription = 'unterschritten';
            break;
          default:
            comparisonDescription = 'Unbekannt';
            break;
        }
        return `Sollwert von ${toCompareValue}°C im ${metricLabel} ${comparisonDescription}`;
      }
      return '-';
    },
    getComponentTypeBySpotId(spotId: string): string {
      if (this.heatingSystem && Array.isArray(this.heatingSystem.heatingSystemMeasurementGroups) && spotId) {
        const group = this.heatingSystem.heatingSystemMeasurementGroups.find((g) => g.spotId === spotId);
        if (group && group.systemComponentTypeName) {
          return getHeatingSystemComponentName(group.systemComponentTypeName);
        }
      }
      return '-';
    },
  },
  apollo: {
    alerts: {
      client: APOLLO_CLIENT.HEATING_SYSTEM_COLLECTOR_CLIENT,
      query: query,
      fetchPolicy: 'network-only',
      manual: true,
      variables(): AlertTicketsByHeatingSystemIdQueryVariables {
        return {
          heatingSystemId: this.heatingSystem?.id || '',
        };
      },
      skip() {
        return !this.heatingSystem?.id;
      },
      result({ data }: { data: AlertTicketsByHeatingSystemIdQuery }) {
        this.alerts = data.alertTicketsByHeatingSystemId || [];
      },
    },
  },
});
